/**
 * HxChatMessage Component
 *
 * Renders a single chat message bubble in a telehealth call chat panel. Supports own/other-side layout, sender name +
 * avatar and timestamp.
 *
 * @example
 *   Received message
 *   ```html
 *   <hx-chat-message
 *   senderName="Dr. Chen"
 *   senderInitials="DC"
 *   message="Hello, can you hear me?"
 *   [timestamp]="msgDate"
 *   />
 *   ```
 *
 * @example
 *   Sent message (right-aligned)
 *   ```html
 *   <hx-chat-message
 *   senderName="You"
 *   message="Yes, loud and clear!"
 *   [timestamp]="msgDate"
 *   [isOwn]="true"
 *   [isRead]="true"
 *   />
 *   ```
 *
 * @package @complexcare/harmony
 */

import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from '@angular/core';

@Component({
  selector: 'hx-chat-message',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './chat-message.html',
  styleUrls: ['./chat-message.css'],
  host: {
    class: 'hx-chat-message',
    '[class.hx-chat-message--own]': 'isOwn()',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxChatMessage {
  /** The message text content */
  readonly message = input.required<string>();

  /** Display name of the sender */
  readonly senderName = input<string>('');

  /** Initials for the avatar fallback */
  readonly senderInitials = input<string>('');

  /** Optional avatar image URL */
  readonly avatarSrc = input<string | null>(null);

  /** Message timestamp */
  readonly timestamp = input<Date | string | null>(null);

  /** Whether this message was sent by the current user (aligns right) */
  readonly isOwn = input<boolean>(false);

  /** Whether the message has been read (shows double-check) */
  readonly isRead = input<boolean>(false);

  /** Whether the message is currently being delivered */
  readonly isPending = input<boolean>(false);

  protected readonly displayInitials = computed(() => {
    if (this.senderInitials()) return this.senderInitials();
    return this.senderName()
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');
  });

  protected readonly timestampDate = computed(() => {
    const t = this.timestamp();
    if (!t) return null;
    return t instanceof Date ? t : new Date(t);
  });
}
